(() => {
  if (window.__voidCartActions) return;
  window.__voidCartActions = true;

  const productCache = new Map();
  let activeController = null;

  /**
   * Collect section IDs from all cart-items-component elements.
   * Falls back to the cart drawer's parent section ID when no
   * cart-items-component is present (e.g. empty cart state).
   */
  const getCartSectionIds = () => {
    const ids = new Set();

    for (const el of document.querySelectorAll(
      "cart-items-component[data-section-id]",
    )) {
      ids.add(el.dataset.sectionId);
    }

    if (ids.size === 0) {
      const drawer = document.querySelector("cart-drawer-component");
      const section = drawer?.closest(".shopify-section");
      const sectionId = section?.id?.replace("shopify-section-", "");
      if (sectionId) ids.add(sectionId);
    }

    return [...ids];
  };

  /**
   * Resolve a product handle (and optional variant title) to a variant ID.
   * If `variantTitle` is provided, the variant matching `title` or `option1`
   * is returned — otherwise the first variant. Returns null on failure so
   * callers can continue with the rest of the bundle.
   */
  const getVariantId = async (handle, variantTitle) => {
    if (!handle) return null;
    const cacheKey = variantTitle ? `${handle}::${variantTitle}` : handle;
    if (productCache.has(cacheKey)) return productCache.get(cacheKey);

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        console.warn(`[void-cart-actions] product not found: ${handle}`);
        return null;
      }
      const product = await response.json();
      let variant;
      if (variantTitle) {
        variant = product.variants?.find(
          (v) => v.title === variantTitle || v.option1 === variantTitle || String(v.sku) === variantTitle,
        );
        if (!variant) {
          console.warn(
            `[void-cart-actions] variant "${variantTitle}" not found on ${handle}, falling back to first`,
          );
        }
      }
      if (!variant) variant = product.variants?.[0];
      if (!variant?.id) return null;
      productCache.set(cacheKey, variant.id);
      return variant.id;
    } catch (err) {
      if (err.name === "AbortError") throw err;
      console.warn(`[void-cart-actions] product fetch failed: ${handle}`, err);
      return null;
    }
  };

  /**
   * POST to /cart/add.js requesting updated section HTML in the same call.
   * Accepts an array of items so a "Pad + Club subscription" pack can be
   * added in a single round-trip.
   */
  const addToCart = async (items) => {
    const sectionIds = getCartSectionIds();
    const payload = { items };

    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(",");
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Cart add failed (${response.status}): ${text}`);
    }

    return response.json();
  };

  /**
   * Activate <script> tags inside a container.
   * Scripts injected via innerHTML are inert — cloning them into fresh
   * <script> elements forces the browser to evaluate them.
   *
   * SECURITY: only re-activate scripts that have a `src` attribute (external
   * modules/assets). Inline scripts are intentionally NOT re-executed to
   * limit blast radius if a stored XSS ever lands in a section template
   * upstream. Inline behavior in cart sections is not used by the theme.
   */
  const activateScripts = (container) => {
    for (const old of container.querySelectorAll("script")) {
      if (!old.src) continue; // skip inline scripts
      const fresh = document.createElement("script");
      for (const attr of old.attributes) {
        fresh.setAttribute(attr.name, attr.value);
      }
      old.replaceWith(fresh);
    }
  };

  /**
   * Directly morph the cart drawer DOM from sections HTML.
   * Required because cart-items-component loads with fetchpriority="low"
   * and may not be upgraded when the cart is initially empty.
   */
  const morphCartDrawer = (drawer, sections) => {
    if (!sections) return;

    const parser = new DOMParser();

    for (const [sectionId, html] of Object.entries(sections)) {
      const currentEl = document.querySelector(
        `cart-items-component[data-section-id="${sectionId}"]`,
      );
      if (!currentEl || !html) continue;

      const doc = parser.parseFromString(html, "text/html");
      const newEl = doc.querySelector(
        `cart-items-component[data-section-id="${sectionId}"]`,
      );
      if (newEl) {
        currentEl.innerHTML = newEl.innerHTML;
        activateScripts(currentEl);
      }

      // Sync dialog classes (removes cart-drawer--empty when cart has items)
      const dialog = drawer.querySelector("dialog");
      const newDialog = doc.querySelector("dialog");
      if (dialog && newDialog) {
        dialog.className = newDialog.className;
      }
    }
  };

  /**
   * Update cart drawer content and open it.
   * 1. Direct DOM morph (works even if cart-items-component is not upgraded)
   * 2. Dispatch cart:update for any upgraded theme components
   * 3. Open the drawer
   */
  const openCartDrawer = (sections, quantity) => {
    const drawer = document.querySelector("cart-drawer-component");
    if (!drawer) {
      window.location.href = "/cart";
      return;
    }

    morphCartDrawer(drawer, sections);

    document.dispatchEvent(
      new CustomEvent("cart:update", {
        bubbles: true,
        detail: {
          resource: null,
          sourceId: null,
          data: {
            source: "void-cart-actions",
            sections: sections ?? null,
            itemCount: quantity,
          },
        },
      }),
    );

    // PostHog: Track cart drawer opened (same distinct_id as pageviews)
    if (window.posthog && window.posthog.capture) {
      posthog.capture('cart_viewed', {
        item_count: quantity,
        source: 'drawer'
      });
    }

    if (!drawer.hasAttribute("auto-open")) {
      if (typeof drawer.open === "function") {
        drawer.open();
      } else if (typeof drawer.showDialog === "function") {
        drawer.showDialog();
      }
    }
  };

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-void-action]");
    if (!btn || btn.disabled) return;

    e.preventDefault();
    e.stopPropagation();

    const handle = btn.dataset.productHandle || "loid-pad";
    const extraHandle = btn.dataset.extraHandle || "";
    const extraVariant = btn.dataset.extraVariant || "";
    const packId = btn.dataset.packId || "";
    const quantity = parseInt(btn.dataset.quantity || "1", 10);
    const originalHTML = btn.innerHTML;

    btn.disabled = true;
    btn.style.opacity = "0.7";
    btn.style.pointerEvents = "none";

    try {
      const [mainVariantId, extraVariantId] = await Promise.all([
        getVariantId(handle),
        extraHandle ? getVariantId(extraHandle, extraVariant) : Promise.resolve(null),
      ]);

      if (!mainVariantId) {
        throw new Error(`Main product not found: ${handle}`);
      }

      // SECURITY: if a subscription was expected but couldn't be resolved,
      // abort the add entirely. Otherwise the customer would pay the full
      // pad price (no auto-discount triggered) thinking they got the bundle.
      if (extraHandle && !extraVariantId) {
        throw new Error(
          `Subscription product/variant not found (handle="${extraHandle}", variant="${extraVariant}"). Bundle add aborted to prevent mispricing.`,
        );
      }

      const items = [
        {
          id: mainVariantId,
          quantity,
          properties: extraVariantId
            ? {
                _pack: packId || "club",
                _variant: extraVariant || "",
              }
            : undefined,
        },
      ];
      if (extraVariantId) {
        items.push({
          id: extraVariantId,
          quantity,
          properties: {
            _pack: packId || "club",
            _variant: extraVariant || "",
          },
        });
      }

      const response = await addToCart(items);

      if (window.posthog && window.posthog.capture) {
        posthog.capture("product_added_to_cart", {
          product_handle: handle,
          extra_handle: extraHandle || null,
          pack_id: packId || null,
          variant_id: mainVariantId,
          extra_variant_id: extraVariantId || null,
          quantity,
        });
      }

      btn.innerHTML = "Ajout\u00e9 \u2713";
      openCartDrawer(response.sections, quantity);

      setTimeout(() => {
        btn.innerHTML = originalHTML;
        btn.disabled = false;
        btn.style.opacity = "";
        btn.style.pointerEvents = "";
      }, 1500);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("[void-cart-actions]", error);
      btn.innerHTML = originalHTML;
      btn.disabled = false;
      btn.style.opacity = "";
      btn.style.pointerEvents = "";
    }
  });

  /**
   * Cleanup orphan subscription line items.
   *
   * Cart drawer hides subscription lines (data-void-sub-line) so when the
   * customer removes the pad, they can't see the subscription is still in
   * the cart. This watcher syncs the cart on every cart:update event:
   * if the pad is gone but lines tagged with `_pack` remain, drop them.
   *
   * Also handles the symmetric edge case where ALL pad units are removed
   * but multiple subscription lines linger.
   */
  const PAD_HANDLE = "loid-pad";
  let cleanupRunning = false;

  const cleanupOrphanSubscriptions = async () => {
    if (cleanupRunning) return;
    cleanupRunning = true;
    try {
      const cart = await fetch("/cart.js", { cache: "no-store" }).then((r) =>
        r.json(),
      );
      if (!cart || !Array.isArray(cart.items)) return;

      const padInCart = cart.items.some(
        (i) => (i.handle || i.product_handle) === PAD_HANDLE && i.quantity > 0,
      );
      if (padInCart) return;

      const orphanKeys = cart.items
        .filter((i) => i.properties && i.properties._pack)
        .map((i) => i.key);
      if (orphanKeys.length === 0) return;

      const updates = {};
      for (const key of orphanKeys) updates[key] = 0;

      const sectionIds = getCartSectionIds();
      const payload = { updates };
      if (sectionIds.length > 0) payload.sections = sectionIds.join(",");

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return;
      const data = await response.json();

      // Refresh the drawer DOM so the badge / hidden lines / totals reflect
      // the new (now empty) cart. Do NOT re-open the drawer.
      const drawer = document.querySelector("cart-drawer-component");
      if (drawer && data.sections) morphCartDrawer(drawer, data.sections);

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: null,
            data: {
              source: "void-cart-actions:cleanup",
              sections: data.sections ?? null,
              itemCount: data.item_count ?? 0,
            },
          },
        }),
      );
    } catch (err) {
      console.warn("[void-cart-actions] orphan cleanup failed", err);
    } finally {
      cleanupRunning = false;
    }
  };

  document.addEventListener("cart:update", (e) => {
    // Avoid recursion: ignore events emitted by our own cleanup pass.
    if (e?.detail?.data?.source === "void-cart-actions:cleanup") return;
    cleanupOrphanSubscriptions();
  });
})();
